import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon } from 'lucide-react';
import { Employee, Lead, LeadSource } from '../../types/pro';
import { toISODate } from '../../utils/pro';
interface NewLeadModalProps {
  isOpen: boolean;
  onClose: () => void;
  employees: Employee[];
  onCreate: (lead: Omit<Lead, 'id'>) => void;
}
const EASE = [0.23, 1, 0.32, 1] as const;
const SOURCES: LeadSource[] = ['Google', 'Facebook', 'Instagram', 'Hjemmeside', 'Telefon', 'Anbefaling', 'E-mail'];
const empty = {
  name: '',
  phone: '',
  email: '',
  address: '',
  city: '',
  jobType: '',
  description: '',
  source: 'Telefon' as LeadSource,
  estimatedValue: '',
  ownerId: ''
};
export function NewLeadModal({
  isOpen,
  onClose,
  employees,
  onCreate
}: NewLeadModalProps) {
  const [form, setForm] = useState(empty);
  const set = (key: keyof typeof empty, value: string) => setForm(current => ({
    ...current,
    [key]: value
  }));
  const canSubmit = form.name.trim() && form.jobType.trim();
  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;
    const today = toISODate(new Date());
    onCreate({
      name: form.name.trim(),
      phone: form.phone.trim(),
      email: form.email.trim(),
      address: form.address.trim(),
      city: form.city.trim(),
      jobType: form.jobType.trim(),
      description: form.description.trim(),
      source: form.source,
      estimatedValue: parseFloat(form.estimatedValue) || 0,
      ownerId: form.ownerId || employees[0]?.id || '',
      stage: 'nyt',
      notes: '',
      createdDate: today,
      lastActivityDate: today,
      activity: [{
        id: `a-${Date.now()}`,
        date: today,
        text: `Lead oprettet manuelt (kilde: ${form.source}).`,
        author: 'Dig'
      }]
    });
    setForm(empty);
    onClose();
  };
  const field = 'w-full rounded-lg border border-stone-200 px-3 py-2.5 text-sm outline-none focus:border-transparent focus:ring-2 focus:ring-brand-500';
  const labelText = 'mb-1.5 block text-sm font-medium text-stone-700';
  return <AnimatePresence data-id="element-459" data-mp="fhw68q4xd71m">
      {isOpen && <div role="dialog" aria-modal="true" aria-labelledby="new-lead-title" className="fixed inset-0 z-[60] flex items-end justify-center p-0 sm:items-center sm:p-6" data-id="element-460" data-mp="1kqss52vt5wc">
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: EASE
      }} onClick={onClose} className="absolute inset-0 bg-stone-900/50" data-id="element-461" data-mp="3nyqx178g70zc" />

          <motion.form onSubmit={submit} initial={{
        opacity: 0,
        y: 20,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 10,
        scale: 0.98
      }} transition={{
        duration: 0.24,
        ease: EASE
      }} className="relative flex max-h-[92vh] w-full flex-col overflow-hidden rounded-t-2xl bg-white shadow-xl sm:max-w-xl sm:rounded-2xl" data-id="element-462" data-mp="3izxl3j8t7i1a">
            <header className="flex items-center justify-between gap-4 border-b border-stone-200 px-5 py-4" data-id="element-463" data-mp="2lgp7sest6ss0">
              <h2 id="new-lead-title" className="text-lg font-bold tracking-tight text-stone-900" data-id="element-464" data-mp="2zg8fx7lme44b">
                Nyt lead
              </h2>
              <button type="button" onClick={onClose} aria-label="Luk" className="-mr-1 rounded-lg p-1.5 text-stone-400 transition-colors hover:bg-stone-100 hover:text-stone-700" data-id="element-465" data-mp="39bika1zhqnl9">
                <XIcon className="h-5 w-5" data-id="element-466" data-mp="3e3a13ksuhey7" />
              </button>
            </header>

            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-5" data-id="element-467" data-mp="3s3bajon6dupa">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-id="element-468" data-mp="3s18a0aoecj6b">
                <label className="block" data-id="element-469" data-mp="3n62p9f6qdajt">
                  <span className={labelText} data-id="element-470" data-mp="1lfkdyvdrg2z0">
                    Navn <span className="text-red-500" data-id="element-471" data-mp="1lesswehshnmz">*</span>
                  </span>
                  <input autoFocus value={form.name} onChange={e => set('name', e.target.value)} placeholder="f.eks. Kasper Nybo" className={field} data-id="element-472" data-mp="1qd452vvk4a8d" />
                </label>
                <label className="block" data-id="element-473" data-mp="12hhv6ybt329n">
                  <span className={labelText} data-id="element-474" data-mp="12ij5h0kzzkqb">
                    Opgavetype <span className="text-red-500" data-id="element-475" data-mp="17gkt7ytdc884">*</span>
                  </span>
                  <input value={form.jobType} onChange={e => set('jobType', e.target.value)} placeholder="f.eks. Installation af ladeboks" className={field} data-id="element-476" data-mp="1bgv6lwjhq0x0" />
                </label>
                <label className="block" data-id="element-477" data-mp="2rv4ktxu0q9a4">
                  <span className={labelText} data-id="element-478" data-mp="2rttgnwwnuzih">Telefon</span>
                  <input type="tel" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="30 21 84 07" className={field} data-id="element-479" data-mp="31zupwckpxtik" />
                </label>
                <label className="block" data-id="element-480" data-mp="2o86eycm7hb36">
                  <span className={labelText} data-id="element-481" data-mp="2o754oad0ksmi">E-mail</span>
                  <input type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="navn@example.dk" className={field} data-id="element-482" data-mp="2j8k429tuo6xj" />
                </label>
                <label className="block" data-id="element-483" data-mp="259bzf1ex46ly">
                  <span className={labelText} data-id="element-484" data-mp="25adm635aqhji">Adresse</span>
                  <input value={form.address} onChange={e => set('address', e.target.value)} placeholder="Enghavevej 33" className={field} data-id="element-485" data-mp="7j0x9362sswm" />
                </label>
                <label className="block" data-id="element-486" data-mp="1wybn98w8vpqh">
                  <span className={labelText} data-id="element-487" data-mp="21robvj0xbwj8">By</span>
                  <input value={form.city} onChange={e => set('city', e.target.value)} placeholder="Grenaa" className={field} data-id="element-488" data-mp="21t7rd7era3jy" />
                </label>
                <label className="block" data-id="element-489" data-mp="2fqny6k0yrzc8">
                  <span className={labelText} data-id="element-490" data-mp="2frp8gma5ohsw">Kilde</span>
                  <select value={form.source} onChange={e => set('source', e.target.value)} className={field} data-id="element-491" data-mp="2kq8zh13lwjs4">
                    {SOURCES.map(source => <option key={source} value={source} data-id="element-492" data-mp="2bnhn8z2wpjzf">
                        {source}
                      </option>)}
                  </select>
                </label>
                <label className="block" data-id="element-493" data-mp="10bgt14n9x3ca">
                  <span className={labelText} data-id="element-494" data-mp="rsvceemqeeta">Forventet værdi (kr)</span>
                  <input type="number" inputMode="numeric" value={form.estimatedValue} onChange={e => set('estimatedValue', e.target.value)} placeholder="14500" className={field} data-id="element-495" data-mp="ruzmjeb84a20" />
                </label>
              </div>

              <label className="block" data-id="element-496" data-mp="93f8rifr268n">
                <span className={labelText} data-id="element-497" data-mp="90az45jimial">Ansvarlig</span>
                <select value={form.ownerId} onChange={e => set('ownerId', e.target.value)} className={field} data-id="element-498" data-mp="455bn2agsvke">
                  <option value="" data-id="element-499" data-mp="e0p39chzx83c">Vælg medarbejder…</option>
                  {employees.map(employee => <option key={employee.id} value={employee.id} data-id="element-500" data-mp="3mg8s7zbmw4cl">
                      {employee.name}
                    </option>)}
                </select>
              </label>

              <label className="block" data-id="element-501" data-mp="2w0ozmwy5w7ja">
                <span className={labelText} data-id="element-502" data-mp="30y8pyv84warl">Beskrivelse</span>
                <textarea value={form.description} onChange={e => set('description', e.target.value)} rows={3} placeholder="Hvad har kunden brug for?" className={field} data-id="element-503" data-mp="30yqsg9le3l7y" />
              </label>
            </div>

            <footer className="flex gap-3 border-t border-stone-200 px-5 py-4" data-id="element-504" data-mp="3evx8vxin57u0">
              <button type="button" onClick={onClose} className="flex-1 rounded-lg border border-stone-200 px-4 py-2.5 text-sm font-medium text-stone-700 transition-colors hover:bg-stone-50" data-id="element-505" data-mp="3jviakfp73uaf">
                Annuller
              </button>
              <button type="submit" disabled={!canSubmit} className="flex-1 rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-stone-200 disabled:text-stone-400" data-id="element-506" data-mp="2ifvia323tif">
                Opret lead
              </button>
            </footer>
          </motion.form>
        </div>}
    </AnimatePresence>;
}